"use client";

import React, { FC, ReactNode, useMemo, useState, useEffect } from "react";
import {
  ConnectionProvider,
  WalletProvider,
} from "@solana/wallet-adapter-react";
import { WalletAdapterNetwork } from "@solana/wallet-adapter-base";
import { WalletModalProvider } from "@solana/wallet-adapter-react-ui";
import { clusterApiUrl } from "@solana/web3.js";
import "@solana/wallet-adapter-react-ui/styles.css";
import {
  PhantomWalletAdapter,
  SolflareWalletAdapter,
  LedgerWalletAdapter,
} from "@solana/wallet-adapter-wallets";
import {
  BridgeWalletAdapter,
  getBridgeClient,
} from "@/lib/wallet-bridge";

interface SolanaProviderProps {
  children: ReactNode;
}

/**
 * Check if we should use the bridge adapters for wallet operations.
 * Returns true only when ALL four conditions are met:
 * 1. We're on the /widget route
 * 2. We're running inside an iframe
 * 3. No native wallet extension is available
 * 4. The bridge responds to a ping within 2000ms
 */
async function shouldUseBridge(): Promise<boolean> {
  // Must be client-side
  if (typeof window === "undefined") return false;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const w = window as any;

  // Condition 1: Must be on /widget route
  if (!window.location.pathname.startsWith("/widget")) {
    return false;
  }

  // Condition 2: Must be inside an iframe
  if (window === window.top) {
    return false;
  }

  // Condition 3: No native wallet extension available
  // NOTE: This may incorrectly skip bridge when Phantom injects into iframe
  if (w.solana || w.phantom?.solana) {
    return false;
  }

  // Condition 4: Bridge responds to ping
  try {
    const bridgeClient = getBridgeClient();
    const pong = await bridgeClient.ping();
    return !!pong;
  } catch {
    return false;
  }
}

export const SolanaProvider: FC<SolanaProviderProps> = ({ children }) => {
  // The network can be set to 'devnet', 'testnet', or 'mainnet-beta'
  const network = WalletAdapterNetwork.Mainnet;

  // Bridge eligibility state - defaults to false (native adapters)
  const [useBridge, setUseBridge] = useState<boolean>(false);

  // Check bridge eligibility on mount (client-side only)
  // Only flip to true if all conditions pass; otherwise stay with native adapters
  useEffect(() => {
    shouldUseBridge().then((result) => {
      if (result) setUseBridge(true);
    });
  }, []);

  // You can also provide a custom RPC endpoint
  const endpoint = useMemo(() => clusterApiUrl(network), [network]);

  // Create wallets based on bridge eligibility
  const wallets = useMemo(() => {
    if (useBridge === true) {
      // Use bridge adapters when eligible
      const bridgeClient = getBridgeClient();
      return [
        new BridgeWalletAdapter({
          walletName: "phantom",
          bridgeClient,
          network,
        }),
        new BridgeWalletAdapter({
          walletName: "solflare",
          bridgeClient,
          network,
        }),
      ];
    }

    // Default: use native wallet adapters
    return [
      new PhantomWalletAdapter(),
      new SolflareWalletAdapter({ network }),
      new LedgerWalletAdapter(),
    ];
  }, [network, useBridge]);

  return (
    <ConnectionProvider endpoint={endpoint}>
      <WalletProvider wallets={wallets} autoConnect>
        <WalletModalProvider>{children}</WalletModalProvider>
      </WalletProvider>
    </ConnectionProvider>
  );
};
